// RecordEditAttachmentSubList - MODULE
// ***********************************************************************************************************************
import downloadjs from "downloadjs";
import * as React from "react";
import { Base } from "ui/Scripts/source/framework/base";
import { IFileAttachment } from "ui/Scripts/source/models/common/fileAttachment";
import { IIdGetTitle } from "ui/Scripts/source/models/common/idTitle";
import { ConfirmationDialogResult } from "ui/Scripts/source/models/enums";
import { Translations } from "ui/Scripts/source/models/translations";
import { EditItem } from "../../../../../../QTeamSystems.QTeamIntra.Ui/Scripts/source/models/common/editItem";
import { AppUtils } from "../../../framework/appUtils";
import { SaveDataObj } from "../../../framework/saveData";
import * as attachmentService from "../../../models/services/attachmentService";
import * as recordService from "../../../models/services/recordService";
import { ActionContainer } from "../../framework/ActionContainer";
import { List } from "../../framework/List/List";
import { ListIconButtonsItem } from "../../framework/List/ListIconButtons/ListIconButtonsItem/ListIconButtonsItem";
import { Box, Typography } from "@mui/material";
import { AttachmentIcon } from "source/components/attachments/AttachmentIcon";

// RecordEditAttachmentSubList
interface IAttachmentLine extends IFileAttachment {
    relation: string;
}

export interface IRecordEditAttachmentSubListProp {
    isReadOnly?: boolean;
    classes?: string;
    companyId: string;
    recordId: string;
    userCode: string;
    title?: string;
    attachments: IFileAttachment[];
    records: IIdGetTitle[];
    onRemoved: (ids: string[]) => void;
    onAdded: (attachments: IFileAttachment[]) => void;
}

interface IRecordEditAttachmentSubListState {
    attachments: IAttachmentLine[];
    selectedId: string;
    sortColumn: string;
    sortOrderIsAsc: boolean;
}

export class RecordEditAttachmentSubList extends React.Component<IRecordEditAttachmentSubListProp, IRecordEditAttachmentSubListState> {
    static sortAttachmentLines(items: IAttachmentLine[], column: string, asc: boolean): IAttachmentLine[] {
        if (!items && items.length < 2) return items;
        if (column === "date") {
            return items.sort((a: IAttachmentLine, b: IAttachmentLine) => { return (!a || !b) ? 0 : ((asc ? 1 : -1) * Base.numCompare(a.modifyDate, b.modifyDate)); });
        } else if (column === "relation") {
            return items.sort((a: IAttachmentLine, b: IAttachmentLine) => { return (!a || !b) ? 0 : ((asc ? 1 : -1) * Base.strCompare(a.relation, b.relation)); });
        } else {
            return items.sort((a: IAttachmentLine, b: IAttachmentLine) => { return (!a || !b) ? 0 : ((asc ? 1 : -1) * Base.strCompare(a.name, b.name)); });
        }
    }

    getFilteredList = (column: string, asc: boolean): IAttachmentLine[] => {
        const props = this.props;
        const result: IAttachmentLine[] = (props.attachments ? props.attachments.slice(0) : [])
            .map(i => {
                const record = props.records.find(j => j.id === i.subOwnerId);
                return {
                    ...i,
                    relation: record ? record.getTitle() : ""
                };
            });
        RecordEditAttachmentSubList.sortAttachmentLines(result, column, asc);
        return result;
    };

    constructor(props: IRecordEditAttachmentSubListProp) {
        super(props);
        this.state = {
            attachments: this.getFilteredList("name", true),
            selectedId: null,
            sortColumn: "name",
            sortOrderIsAsc: true
        };
    }

    componentDidUpdate(prevProps: IRecordEditAttachmentSubListProp, prevState: IRecordEditAttachmentSubListState) {
        const props = this.props;
        const state = this.state;
        if (EditItem.getHash(prevProps.attachments) === EditItem.getHash(props.attachments)) return;
        this.filteredList(state.sortColumn, state.sortOrderIsAsc);
    };

    handleSetSelectedIds = (id: string) => {
        this.setState({
            selectedId: id
        });
    };

    filteredList = (column: string, asc: boolean) => {
        this.setState({
            sortColumn: column,
            sortOrderIsAsc: asc,
            attachments: this.getFilteredList(column, asc)
        });
    };

    handleSearchLines = (sortColumn: string, sortOrderIsAsc: boolean, nextPage: boolean) => {
        this.filteredList(sortColumn, sortOrderIsAsc);
    };

    handleDownload = async () => {
        const selectedId = this.state.selectedId;
        if (!selectedId) return;
        const props = this.props;
        const attachment = props.attachments.find(i => i.id === selectedId);
        if (!attachment) return;
        if (attachment.file) {
            downloadjs(attachment.file, attachment.name);
            return;
        }
        if (attachment.isNew()) return;
        const fileName = await AppUtils.callService(() => attachmentService.getAttachment(selectedId));
        if (!fileName) return;
        AppUtils.showSuccessMessage(String.format(Translations.AttachmentParameterDownloaded, fileName));
    };

    getRemoveSaveData = (props: IRecordEditAttachmentSubListProp, attachmentIds: string[]): SaveDataObj => {
        const result = new SaveDataObj();
        result.append("id", props.recordId);
        result.append("companyId", props.companyId);
        result.append("userCode", props.userCode);
        result.append("ids", attachmentIds);
        return result;
    };

    handleRemove = async () => {
        const selectedId = this.state.selectedId;
        if (!selectedId) return;
        const attachment = this.state.attachments.find(i => i.id === selectedId);
        if (!attachment) return;
        if (await AppUtils.showConfirmationDialog(String.format(Translations.DoYouReallyWantToRemoveAttachmentParameter, attachment.name + " / " + Base.timeToDateStr(attachment.modifyDate) + " / " + attachment.relation), false) === ConfirmationDialogResult.Yes) {
            const props = this.props;
            const saveData = this.getRemoveSaveData(this.props, [selectedId]);
            const result = AppUtils.callService(() => recordService.recordEditRemoveAttachments(saveData.obj));
            if (!result) return;
            props.onRemoved([selectedId]);
        }
    };

    render() {
        const state = this.state;
        return (
            <>
                <ActionContainer
                    margin="0 0 .5rem 0"
                    leftItems={
                        <></>
                    }
                    rightItems={
                        <ListIconButtonsItem
                            downloadTooltip={Translations.Download}
                            removeTooltip={Translations.Remove}
                            downloadDisabled={!state.selectedId}
                            removeDisabled={!state.selectedId}
                            onDownload={this.handleDownload}
                            onRemove={this.handleRemove}
                        />
                    }
                />
                <List
                    intialized={true}
                    isLoading={false}
                    hasMore={false}
                    globalLoadingInUse={false}
                    header={{
                        columns: [
                            { size: 6, column: "name", content: Translations.Name }, // "col-xl-6 col-6"
                            { size: 2, column: "date", content: Translations.Date }, // "col-xl-2 col-2"
                            { size: 4, column: "relation", content: Translations.Relation }, // "col-xl-4 col-4"
                        ]
                    }}
                    lines={
                        state.attachments.map((line) => {
                            return {
                                columns: [
                                    {
                                        size: 6,
                                        content:
                                            <Box display="flex" alignItems="center">
                                                <AttachmentIcon fileType={line.type} />
                                                <Typography sx={{ marginLeft: ".5rem" }}>{line.name}</Typography>
                                            </Box>
                                    },
                                    { size: 2, content: Base.timeToDateStr(line.modifyDate) },
                                    { size: 4, content: line.relation }
                                ],
                                id: line.id
                            };
                        })
                    }
                    sortColumn={state.sortColumn}
                    sortOrderIsAsc={state.sortOrderIsAsc}
                    selectedId={state.selectedId}
                    onSetSelectedIds={this.handleSetSelectedIds}
                    onSearchLines={this.handleSearchLines}
                />
            </>
        );
    }
}
